import { Typography, TypographyProps } from "@mui/material";
import { ReactNode } from "react";

import PageTabsV2 from "@/App/Layouts/PageBody/PageTabsV2";
import SimpleViewPageHeader from "@/App/Layouts/PageHeader/SimpleViewPageHeader";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import { EntityChipList } from "@/common/components/EntityInfo/EntityChipList";
import { AppModalCloseIconButton } from "@/common/components/Modals/AppModalCloseIconButton";
import { PersistenceKey } from "@/common/constants/persistenceKey";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useMyTenantAffiliation } from "@/common/hooks/entity/tenant/useMyTenantAffiliation";
import { useCommonViewParams } from "@/common/hooks/layout/useCommonViewParams";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { EntityViewVariant, ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  DataUpdatesHubClientMethodName,
  EntityType,
  VehicleDamageDto,
} from "@/core/api/generated/v1";

import AppLink from "../../../Link/AppLink";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import { VehicleDamageMenu } from "../VehicleDamageMenu";
import GeneralHistoryTabContent from "./Tabs/GeneralHistoryTabContent";
import OverviewTabContent from "./Tabs/OverviewTabContent";
import VehicleTabContent from "./Tabs/VehicleTabContent";

export enum VehicleDamageViewPageTabs {
  Overview = "Overview",
  Vehicle = "Vehicle",
  GeneralHistory = "GeneralHistory",
}

export const defaultHeaderProps = {
  withLink: false,
  typographyProps: undefined as unknown as Partial<TypographyProps<"div">> | undefined,
  title: undefined as ReactNode | undefined,
};

export const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  delete: true,
  /** For modal view.  */
  closeInHeader: true,
  inspector: true,
  originalDamage: true,
  contract: true,
  damageDetection: true,
  damageCostEvaluation: true,
  repairOperation: true,
  visualModel: true,
  viewVariant: ViewVariant.Page,
};

interface OwnProps extends BaseEntityViewInheritableProps<VehicleDamageDto> {
  vehicleId: string | null | undefined;
  vehicleDamageId: string | null | undefined;
  vehicleDamage?: VehicleDamageDto | null;
  headerProps?: Partial<typeof defaultHeaderProps>;
  displayProps?: Partial<typeof defaultDisplayProps>;
}

export type VehicleDamageViewProps = OwnProps;

export default function VehicleDamageView({
  vehicleId,
  vehicleDamageId,
  vehicleDamage,
  headerProps = defaultHeaderProps,
  displayProps = defaultDisplayProps,
  onDelete,
  onClose,
}: VehicleDamageViewProps) {
  displayProps = { ...defaultDisplayProps, ...displayProps };

  const history = useAppHistory();

  const commonViewParams = useCommonViewParams({
    statePersistence: {
      persistenceKey: PersistenceKey.forEntityView(EntityType.VehicleDamage),
      viewVariant: displayProps.viewVariant,
    },
  });

  const request = useApiRequest(
    apiClient.vehicleDamagesApi.apiV1VehicleDamagesVehicleDamageIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      vehicleDamageId: vehicleDamageId!,
    },
    {
      deps: [vehicleDamageId],
      skip: !!vehicleDamage || !vehicleDamageId,
    },
  );
  vehicleDamage = vehicleDamage || request?.data || undefined;

  const myTenantAffiliation = useMyTenantAffiliation({ tenantId: vehicleDamage?.tenantId });

  useRealtimeDataUpdates({
    channelNames: [
      DataUpdatesChannelName.Entity(undefined, EntityType.VehicleDamage, vehicleDamageId || ""),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      request.handleEntityChanged(data);
    },
  });

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumbs:
      (displayProps?.breadcrumbs &&
        vehicleDamage && [
          {
            idValue: vehicleDamage?.id || "",
            newTitle: vehicleDamage?.localNumber || "",
          },
          {
            idValue: vehicleDamage?.vehicle?.id || "",
            newTitle:
              vehicleDamage?.vehicle?.plateNo?.plateNo ||
              vehicleDamage?.vehicle?.identificationNumber ||
              vehicleDamage?.vehicle?.localNumber ||
              "",
          },
        ]) ||
      undefined,
  });

  return (
    <BaseEntityView
      entityType={EntityType.VehicleDamage}
      entityId={vehicleDamageId}
      entity={vehicleDamage}
      entityRequest={request}
    >
      <ViewLayout
        displayProps={displayProps}
        header={
          displayProps.header &&
          vehicleDamage && (
            <SimpleViewPageHeader
              title={
                <Typography
                  component='div'
                  variant='h1'
                  sx={{ display: "flex", alignItems: "center" }}
                  {...headerProps?.typographyProps}
                >
                  {headerProps?.title || (
                    <>
                      <span>Vehicle damage </span>&nbsp;
                      <AppLink
                        isEnabled={headerProps?.withLink || false}
                        to={ROUTE_PATH.VEHICLE_DAMAGE_VIEW({
                          vehicleId: vehicleDamage?.vehicle?.id || undefined,
                          vehicleDamageId: vehicleDamage?.id || undefined,
                        })}
                      >
                        <span>{vehicleDamage?.localNumber}</span>
                      </AppLink>
                    </>
                  )}{" "}
                  <EntityChipList
                    entity={vehicleDamage}
                    entityViewVariant={EntityViewVariant.View}
                    variant='normal'
                    sx={{ ml: 1 }}
                  />
                </Typography>
              }
              secondaryActions={
                displayProps.actions &&
                vehicleDamage && (
                  <VehicleDamageMenu
                    entity={vehicleDamage}
                    displayProps={{
                      actions: {
                        quickView: false,
                        addToRepairOperation: true,
                        migrateToNewVisualModel: true,
                        delete: displayProps.delete ?? true,
                      },
                    }}
                    onUpdate={(newValue) =>
                      newValue ? request.replaceData(newValue) : request.refetch()
                    }
                    onDelete={() => (onDelete ? onDelete() : history.goBack())}
                  />
                )
              }
              closeAction={
                displayProps.closeInHeader && onClose ? (
                  <AppModalCloseIconButton onClick={onClose} />
                ) : undefined
              }
            />
          )
        }
      >
        <PageTabsV2
          tabIdsDefinition={VehicleDamageViewPageTabs}
          defaultTabId={VehicleDamageViewPageTabs.Overview}
          viewVariant={displayProps?.viewVariant}
          commonViewParams={commonViewParams}
          tabs={[
            { label: "Overview", value: VehicleDamageViewPageTabs.Overview, isHideable: false },
            {
              label: "Vehicle",
              value: VehicleDamageViewPageTabs.Vehicle,
              permissions: [AppPermission.VehicleRead],
            },
            {
              label: "History",
              value: VehicleDamageViewPageTabs.GeneralHistory,
              if: myTenantAffiliation.isMyTenant,
              permissions: [AppPermission.GeneralHistoryRead],
            },
          ]}
        >
          {({ activeTabId: activeTab }) =>
            vehicleDamage && (
              <>
                {activeTab === VehicleDamageViewPageTabs.Overview && (
                  <OverviewTabContent entity={vehicleDamage} displayProps={displayProps} />
                )}
                {activeTab === VehicleDamageViewPageTabs.Vehicle && (
                  <VehicleTabContent entity={vehicleDamage} />
                )}
                {activeTab === VehicleDamageViewPageTabs.GeneralHistory && (
                  <GeneralHistoryTabContent entity={vehicleDamage} />
                )}
              </>
            )
          }
        </PageTabsV2>
      </ViewLayout>
    </BaseEntityView>
  );
}
